import React, { useState, useEffect, useContext } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { FaStar, FaStarHalfAlt, FaRegStar } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { productService } from "../services/productService";
import { useTranslation } from "react-i18next";
import ProductDetailModal from "../components/ProductModalDetail";
import { CartContext } from "../context/CartContext";

const FEATURED_CACHE_KEY = "featured_products_cache";
const FEATURED_CACHE_TIMESTAMP_KEY = "featured_products_timestamp";
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const FeatureSection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationDirection, setAnimationDirection] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const { cart, addToCart, loadingItems } = useContext(CartContext);

  const productsPerPage = 2;

  // Check if cache is valid
  const isCacheValid = () => {
    const timestamp = localStorage.getItem(FEATURED_CACHE_TIMESTAMP_KEY);
    if (!timestamp) return false;
    const now = Date.now();
    return (now - parseInt(timestamp)) < CACHE_DURATION;
  };

  // Load from cache
  const loadFromCache = () => {
    try {
      const cachedData = localStorage.getItem(FEATURED_CACHE_KEY);
      if (cachedData) {
        const parsedData = JSON.parse(cachedData);
        setProducts(parsedData);
        setIsLoading(false);
        return true;
      }
    } catch (error) {
      console.error("Error loading from cache:", error);
    }
    return false;
  };

  // Save to cache
  const saveToCache = (data) => {
    try {
      localStorage.setItem(FEATURED_CACHE_KEY, JSON.stringify(data));
      localStorage.setItem(FEATURED_CACHE_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.error("Error saving to cache:", error);
    }
  };

  // Fetch featured products
  const fetchFeaturedProducts = async () => {
    try {
      setIsLoading(true);

      // First, try to load from cache
      if (loadFromCache() && isCacheValid()) {
        // Cache is valid, still fetch in background
        fetchInBackground();
        return;
      }

      // Cache expired or not found, fetch fresh
      await fetchFreshData();

    } catch (error) {
      console.error("Error fetching featured products:", error);
      // Fallback to cache even if expired
      loadFromCache();
      setIsLoading(false);
    }
  };

  // Fetch fresh data from API
  const fetchFreshData = async () => {
    try {
      const allProducts = await productService.getAllProducts();

      const specialBrandProducts = allProducts.filter(
        product => product.collection === "Special Brand"
      );

      let featuredProducts = specialBrandProducts;
      if (featuredProducts.length === 0) {
        featuredProducts = allProducts.filter(
          product => product.collection &&
            product.collection.toLowerCase() === "special brand"
        );
      }

      if (featuredProducts.length === 0) {
        featuredProducts = allProducts.filter(
          product => product.collection && product.collection.length > 0
        );
      }

      if (featuredProducts.length > 0) {
        featuredProducts.sort((a, b) =>
          new Date(b.createdAt || b._id) - new Date(a.createdAt || a._id)
        );
        const limitedProducts = featuredProducts.slice(0, 6);
        setProducts(limitedProducts);
        saveToCache(limitedProducts);
      } else {
        setProducts([]);
        saveToCache([]);
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching fresh data:", error);
      setIsLoading(false);
      // Try cache fallback
      if (!loadFromCache()) {
        setProducts([]);
      }
    }
  };

  // Background fetch (silent update)
  const fetchInBackground = async () => {
    try {
      const allProducts = await productService.getAllProducts();

      const specialBrandProducts = allProducts.filter(
        product => product.collection === "Special Brand"
      );

      let featuredProducts = specialBrandProducts;
      if (featuredProducts.length === 0) {
        featuredProducts = allProducts.filter(
          product => product.collection &&
            product.collection.toLowerCase() === "special brand"
        );
      }

      if (featuredProducts.length === 0) {
        featuredProducts = allProducts.filter(
          product => product.collection && product.collection.length > 0
        );
      }

      if (featuredProducts.length > 0) {
        featuredProducts.sort((a, b) =>
          new Date(b.createdAt || b._id) - new Date(a.createdAt || a._id)
        );
        const limitedProducts = featuredProducts.slice(0, 6);

        // Only update if different
        const currentProductsString = JSON.stringify(products);
        const newProductsString = JSON.stringify(limitedProducts);
        if (currentProductsString !== newProductsString) {
          setProducts(limitedProducts);
          saveToCache(limitedProducts);
        }
      }
    } catch (error) {
      console.error("Background fetch failed:", error);
      // Silent fail for background fetch
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchFeaturedProducts();
  }, []);

  // Background refresh every 5 minutes
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      fetchInBackground();
    }, 5 * 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, [products]);

  // Rest of your component remains the same...
  const totalPages = Math.ceil(products.length / productsPerPage);
  const currentProducts = products.slice(
    currentPage * productsPerPage,
    (currentPage + 1) * productsPerPage
  );

  const handleNextPage = () => {
    if (currentPage < totalPages - 1 && !isAnimating) {
      setAnimationDirection("slide-left");
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentPage(currentPage + 1);
        setTimeout(() => {
          setIsAnimating(false);
          setAnimationDirection("");
        }, 100);
      }, 300);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0 && !isAnimating) {
      setAnimationDirection("slide-right");
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentPage(currentPage - 1);
        setTimeout(() => {
          setIsAnimating(false);
          setAnimationDirection("");
        }, 100);
      }, 300);
    }
  };

  const handleDotClick = (index) => {
    if (index !== currentPage && !isAnimating) {
      const direction = index > currentPage ? "slide-left" : "slide-right";
      setAnimationDirection(direction);
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentPage(index);
        setTimeout(() => {
          setIsAnimating(false);
          setAnimationDirection("");
        }, 100);
      }, 300);
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<FaStar key={`star-${i}`} color="#ffc107" />);
    }
    if (hasHalfStar) {
      stars.push(<FaStarHalfAlt key="half-star" color="#ffc107" />);
    }
    while (stars.length < 5) {
      stars.push(<FaRegStar key={`empty-${stars.length}`} color="#ffc107" />);
    }
    return stars;
  };

  const getEffectivePrice = (product) => {
    const hasProductDiscount = product.discount && parseFloat(product.discount) > 0;

    if (product.bottleVariants && product.bottleVariants.length > 0) {
      const firstValidVariant = product.bottleVariants.find(variant => {
        const hasSellingPrice = variant.sellingPrice && parseFloat(variant.sellingPrice) > 0;
        const hasBottleSize = variant.bottleSize && parseFloat(variant.bottleSize) > 0;
        const hasStock = variant.stock && parseFloat(variant.stock) > 0;
        const hasValidCapacityVariants = variant.capacityVariants &&
          variant.capacityVariants.some(c =>
            c.capacity && c.capacity.trim() !== '' &&
            c.price && parseFloat(c.price) > 0
          );
        return hasSellingPrice || hasBottleSize || hasStock || hasValidCapacityVariants;
      });

      if (firstValidVariant) {
        const sellingPrice = parseFloat(firstValidVariant.sellingPrice) || 0;
        const discountValue = parseFloat(firstValidVariant.discountValue) || 0;
        let finalPrice = sellingPrice;

        if (discountValue > 0) {
          if (firstValidVariant.discountType === 'percentage') {
            finalPrice = sellingPrice - (sellingPrice * discountValue / 100);
          } else {
            finalPrice = sellingPrice - discountValue;
          }
        }

        if (sellingPrice === 0 && firstValidVariant.capacityVariants && firstValidVariant.capacityVariants.length > 0) {
          const firstCapacity = firstValidVariant.capacityVariants.find(c => c.price && parseFloat(c.price) > 0);
          if (firstCapacity) {
            finalPrice = parseFloat(firstCapacity.price) || 0;
          }
        }

        if (finalPrice === 0) {
          finalPrice = product.price || 0;
        }

        const hasVariantDiscount = discountValue > 0 && sellingPrice > 0;
        const useProductDiscount = !hasVariantDiscount && hasProductDiscount && product.price > 0;

        if (useProductDiscount) {
          const productDiscountValue = parseFloat(product.discount) || 0;
          const discountedPrice = finalPrice - (finalPrice * productDiscountValue / 100);

          return {
            price: discountedPrice,
            originalPrice: finalPrice,
            hasDiscount: true,
            discountPercent: productDiscountValue,
            isFromBottle: true,
            bottleSize: firstValidVariant.bottleSize || null,
            displayDiscount: productDiscountValue
          };
        }

        return {
          price: finalPrice,
          originalPrice: sellingPrice || product.price || 0,
          hasDiscount: hasVariantDiscount || hasProductDiscount,
          discountPercent: hasVariantDiscount ?
            (firstValidVariant.discountType === 'percentage' ? discountValue : (discountValue / sellingPrice) * 100) :
            (product.discount || 0),
          isFromBottle: true,
          bottleSize: firstValidVariant.bottleSize || null,
          displayDiscount: hasVariantDiscount ?
            (firstValidVariant.discountType === 'percentage' ? discountValue : Math.round((discountValue / sellingPrice) * 100)) :
            (product.discount || 0)
        };
      }
    }

    let finalPrice = product.price || 0;
    let originalPrice = product.price || 0;
    let hasDiscount = hasProductDiscount;
    let discountPercent = hasProductDiscount ? parseFloat(product.discount) : 0;
    let displayDiscount = hasProductDiscount ? parseFloat(product.discount) : 0;

    if (hasProductDiscount && finalPrice > 0) {
      finalPrice = finalPrice - (finalPrice * discountPercent / 100);
    }

    return {
      price: finalPrice,
      originalPrice: originalPrice,
      hasDiscount: hasDiscount,
      discountPercent: discountPercent,
      isFromBottle: false,
      bottleSize: null,
      displayDiscount: displayDiscount
    };
  };

  const getDisplayPrice = (product) => {
    const effectivePrice = getEffectivePrice(product);

    if (effectivePrice.hasDiscount && effectivePrice.discountPercent > 0) {
      return {
        original: effectivePrice.originalPrice,
        discounted: effectivePrice.price,
        hasDiscount: true,
        discountPercent: effectivePrice.discountPercent,
        displayDiscount: effectivePrice.displayDiscount,
        isFromBottle: effectivePrice.isFromBottle,
        bottleSize: effectivePrice.bottleSize
      };
    }
    return {
      original: effectivePrice.price,
      discounted: effectivePrice.price,
      hasDiscount: false,
      discountPercent: 0,
      displayDiscount: 0,
      isFromBottle: effectivePrice.isFromBottle,
      bottleSize: effectivePrice.bottleSize
    };
  };

  const getProductImage = (product) => {
    if (product.images && product.images.length > 0) {
      return product.images[0];
    }
    return product.image || "https://via.placeholder.com/300x300?text=Product";
  };

  const getProductName = (product) => {
    return product.title || product.name || "Premium Product";
  };

  const getProductDescription = (product) => {
    return product.description || "Discover our amazing product with premium quality and affordable price.";
  };

  const isProductInCart = (productId) => {
    if (!cart || cart.length === 0 || !productId) return false;
    return cart.some((cartItem) => cartItem.product?._id === productId);
  };

  const handleAddToCart = async (product) => {
    try {
      await addToCart(product, product.quantity || 1);
    } catch (error) {
      console.error("Error adding to cart:", error);
    }
  };

  const handleShowModal = (product) => {
    setSelectedProduct(product);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedProduct(null);
  };

  if (isLoading) {
    return (
      <section className="feature-section">
        <div className="feature-overlay">
          <Container>
            <div className="text-center text-white">
              <div className="spinner-border text-light" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading featured products...</p>
            </div>
          </Container>
        </div>
      </section>
    );
  }

  if (products.length === 0) {
    return null;
  }

  return (
    <>
      <section className="feature-section">
        <div className="feature-overlay">
          <Container>
            <h2 className="feature-heading"> {t('Special Brands')} </h2>
            <div className="feature-line"></div>

            <div className="feature-products-wrapper">
              <Row className="g-2 g-md-4">
                {currentProducts.map((product, idx) => {
                  const priceInfo = getDisplayPrice(product);
                  const productImage = getProductImage(product);
                  const productName = getProductName(product);
                  const productDescription = getProductDescription(product);
                  const productId = product._id || product.id;

                  return (
                    <Col lg={6} md={12} key={productId || idx}>
                      <div
                        className={`feature-card ${isAnimating ? `animate-${animationDirection}` : ""}`}
                        style={{ animationDelay: `${idx * 0.1}s` }}
                      >
                        <div className="feature-image-box">
                          <img
                            src={productImage}
                            alt={productName}
                            className="feature-product-img"
                            onError={(e) => {
                              e.target.src = "https://via.placeholder.com/300x300?text=Product";
                            }}
                          />
                          {priceInfo.hasDiscount && priceInfo.displayDiscount > 0 && (
                            <span className="feature-discount-badge" style={{
                              position: 'absolute',
                              top: '12px',
                              left: '12px',
                              background: '#ff3d3d',
                              color: 'white',
                              padding: '4px 12px',
                              borderRadius: '20px',
                              fontSize: '0.75rem',
                              fontWeight: '700',
                              zIndex: 2,
                              boxShadow: '0 2px 8px rgba(255, 61, 61, 0.3)'
                            }}>
                              -{Math.round(priceInfo.displayDiscount)}%
                            </span>
                          )}
                        </div>
                        <div className="feature-content">
                          <div className="feature-stars">
                            {renderStars(product.rating || 4.5)}
                          </div>
                          <h3 className="feature-product-title">
                            {productName}
                          </h3>
                          <p className="feature-desc">
                            {productDescription.substring(0, 80)}
                            {productDescription.length > 80 ? "..." : ""}
                          </p>
                          <div className="feature-price-wrapper" style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            flexWrap: 'wrap',
                            margin: '8px 0 12px 0'
                          }}>
                            {priceInfo.hasDiscount ? (
                              <>
                                <span className="feature-original-price" style={{
                                  fontSize: '1rem',
                                  color: '#a0aec0',
                                  textDecoration: 'line-through',
                                  fontWeight: '400'
                                }}>
                                  ${priceInfo.original.toFixed(2)}
                                </span>
                                <span className="feature-discount-price" style={{
                                  fontSize: '2rem',
                                  fontWeight: '700',
                                  color: '#ff3d3d'
                                }}>
                                  ${priceInfo.discounted.toFixed(2)}
                                </span>
                              </>
                            ) : (
                              <span className="feature-price" style={{
                                fontSize: '2rem',
                                fontWeight: '700',
                                color: '#ff3d3d'
                              }}>
                                ${(priceInfo.original || 0).toFixed(2)}
                              </span>
                            )}
                          </div>
                          <button
                            className="feature-btn"
                            onClick={() => {
                              if (productId) {
                                handleShowModal(product);
                              }
                            }}
                          >
                            Shop Now
                          </button>
                        </div>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </div>

            {totalPages > 1 && (
              <div className="feature-dots">
                <button
                  className={`feature-nav-btn ${currentPage === 0 ? "disabled" : ""}`}
                  onClick={handlePrevPage}
                  disabled={currentPage === 0 || isAnimating}
                >
                  Prev
                </button>
                <div className="dots-container">
                  {Array.from({ length: totalPages }).map((_, idx) => (
                    <div
                      key={idx}
                      className={`feature-dot ${currentPage === idx ? "active" : ""}`}
                      onClick={() => handleDotClick(idx)}
                    />
                  ))}
                </div>
                <button
                  className={`feature-nav-btn ${currentPage === totalPages - 1 ? "disabled" : ""}`}
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages - 1 || isAnimating}
                >
                  Next
                </button>
              </div>
            )}
          </Container>
        </div>
      </section>

      {selectedProduct && (
        <ProductDetailModal
          show={showModal}
          onHide={handleCloseModal}
          product={selectedProduct}
          onAddToCart={handleAddToCart}
          isInCart={selectedProduct ? isProductInCart(selectedProduct._id) : false}
          loadingItems={loadingItems || {}}
        />
      )}
    </>
  );
};

export default FeatureSection;
